import { compact, last, map, orderBy, size } from 'es-toolkit/compat';
import { core } from '@tock/coretypes';

import * as React from 'react';
import { DisplayDate, DisplayDateProps } from './DisplayDate';
import { LocalDate } from '@js-joda/core';

/**
 * Gets a ReactNode about the repetitions on a schedule.
 * The `earliestDate` param, if defined, will be used in place of individual repetetion.repeatsFromDates
 * in a utility sub-function below.
 */
function getRepetitionsPart(
  { repetition }: core.Schedule,
  config: Omit<DisplayDateProps, 'dateValue'>,
  filterDatesEarlierThan: LocalDate | undefined
): React.ReactNode {
  if (repetition?.length) {
    let mapResult = map(repetition, (rep, idx) =>
      getRepetitionPart(rep, config, filterDatesEarlierThan, `repetition-${idx}`)
    );
    // That may include some internal nulls, filter them out.
    mapResult = compact(mapResult);
    if (mapResult.length) {
      return mapResult;
    }
    // But just return null, below, if that filtered out all real results.
  }

  return null;
}

function getRepetitionPart(
  {
    repeatsFromDate,
    repeatsUntilDate,
    repeatsUntilNDays,
    repeatsEvery,
    repeatsOn,
  }: core.IScheduleRepetition,
  config: Omit<DisplayDateProps, 'dateValue'>,
  filterDatesEarlierThan: LocalDate | undefined,
  key: string
): React.ReactNode {
  if (!repeatsFromDate) {
    return null;
  }

  let repeatsFromLocalDate = LocalDate.parse(repeatsFromDate);
  if (filterDatesEarlierThan && filterDatesEarlierThan.isAfter(repeatsFromLocalDate)) {
    repeatsFromLocalDate = filterDatesEarlierThan;
  }
  const repeatsUntilLocalDate = repeatsUntilDate ? LocalDate.parse(repeatsUntilDate) : undefined;

  // Given that repeatsFromLocalDate may have been moved forward in time, it's possible that by now the
  // two dates are the same. If so, there's only one remaining instance of the event to talk about, return that.
  if (repeatsUntilLocalDate && repeatsFromLocalDate.isEqual(repeatsUntilLocalDate)) {
    return (
      <span key={key}>
        <DisplayDate {...config} dateValue={repeatsFromLocalDate} />
      </span>
    );
  }

  const repetitionSpansOverMultipleYears =
    repeatsUntilLocalDate && repeatsFromLocalDate.year() !== repeatsUntilLocalDate.year();

  const repeatsUntilDaysText =
    (repeatsUntilNDays ?? 0) > 0 ? <> ({repeatsUntilNDays} days rolling)</> : undefined;

  if (repeatsEvery === 'MONTH') {
    return (
      <span key={key}>
        Monthly from{' '}
        <DisplayDate
          {...config}
          showYear={repetitionSpansOverMultipleYears}
          dateValue={repeatsFromLocalDate}
        />
        {repeatsUntilLocalDate && (
          <>
            {' '}
            to <DisplayDate {...config} dateValue={repeatsUntilLocalDate} />
          </>
        )}
        {repeatsUntilDaysText}
      </span>
    );
  }
  if (repeatsEvery === 'WEEK') {
    const intraWeekOccurrences = repeatsOn?.nthWeekday?.length ?? 0;
    const frequency =
      intraWeekOccurrences <= 1
        ? 'Weekly'
        : intraWeekOccurrences === 2
        ? 'Twice a week'
        : 'Multiple dates';
    const betweenOrFrom = intraWeekOccurrences <= 1 ? 'from' : 'between';
    const toOrAnd = intraWeekOccurrences <= 1 ? 'to' : 'and';

    return (
      <span key={key}>
        {frequency} {betweenOrFrom}{' '}
        <DisplayDate
          {...config}
          showYear={repetitionSpansOverMultipleYears}
          dateValue={repeatsFromLocalDate}
        />
        {repeatsUntilLocalDate && (
          <>
            {' '}
            {toOrAnd} <DisplayDate {...config} dateValue={repeatsUntilLocalDate} />
          </>
        )}
        {repeatsUntilDaysText}
      </span>
    );
  }
  if (repeatsEvery === 'DAY') {
    return (
      <span key={key}>
        Daily from{' '}
        <DisplayDate
          {...config}
          showYear={repetitionSpansOverMultipleYears}
          dateValue={repeatsFromLocalDate}
        />
        {repeatsUntilLocalDate && (
          <>
            {' '}
            to <DisplayDate {...config} dateValue={repeatsUntilLocalDate} />
          </>
        )}
        {repeatsUntilDaysText}
      </span>
    );
  }
  return null;
}

function getDiscreteDatesPart(
  { date }: core.Schedule,
  config: Omit<DisplayDateProps, 'dateValue'>,
  filterDatesEarlierThan: LocalDate | undefined
): React.ReactNode {
  let orderedDates = compact(
    orderBy(compact(date.map((d) => (d.date ? LocalDate.parse(d.date) : null))), (d) =>
      d.toEpochDay()
    )
  );
  if (filterDatesEarlierThan) {
    orderedDates = orderedDates.filter((date) => !date.isBefore(filterDatesEarlierThan));
  }

  const datesCount = size(orderedDates);

  const minLocalDate = orderedDates[0];
  const maxLocalDate = last(orderedDates);
  if (datesCount === 0 || !maxLocalDate) {
    // That second check is redundant but makes the type checker happier
    return null;
  }

  const repetitionSpansOverMultipleYears = minLocalDate.year() !== maxLocalDate.year();

  if (datesCount === 1) {
    return <DisplayDate {...config} showWeekday dateValue={minLocalDate} />;
  }

  if (datesCount === 2) {
    return (
      <>
        <DisplayDate
          {...config}
          showWeekday
          showYear={repetitionSpansOverMultipleYears}
          dateValue={minLocalDate}
        />
        {' and '}
        <DisplayDate {...config} showWeekday dateValue={maxLocalDate} />
      </>
    );
  }

  return (
    <>
      Multiple dates between{' '}
      <DisplayDate
        {...config}
        showYear={repetitionSpansOverMultipleYears}
        dateValue={minLocalDate}
      />
      {' and '}
      <DisplayDate {...config} dateValue={maxLocalDate} />
    </>
  );
}

interface Props {
  schedule: core.Schedule;
  displayDateOptions?: Omit<DisplayDateProps, 'dateValue'>;
  filterDatesEarlierThan?: LocalDate;
}

const displayDateDefaults: Omit<DisplayDateProps, 'dateValue'> = {
  showDay: true,
  showMonth: true,
  showYear: true,
  displayDayAs: 'numeric',
  displayYearAs: 'numeric',
  displayMonthAs: 'short',
  displayWeekdayAs: 'long',
};

/**
 * Displays a date, without the time, in the business locale.
 */
export const DisplaySchedule = ({
  schedule,
  displayDateOptions,
  filterDatesEarlierThan,
}: Props) => {
  const config = {
    ...displayDateDefaults,
    ...displayDateOptions,
  };

  return (
    <>
      {getRepetitionsPart(schedule, config, filterDatesEarlierThan) ||
        getDiscreteDatesPart(schedule, config, filterDatesEarlierThan)}
    </>
  );
};
